import { useState, ChangeEvent, FormEvent } from "react";
import { usePromiseTracker, trackPromise } from "react-promise-tracker";
import axiosClient from "../../axiosClient";
import { ClipLoader } from "react-spinners";
import { Link } from "react-router-dom";
import { CheckCircleIcon } from "@heroicons/react/24/outline";
import {
  ArrowLongRightIcon,
  ArrowLongLeftIcon,
} from "@heroicons/react/24/solid";

const ForgotPassword = () => {
  const [email, setEmail] = useState("");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [passwordResetEmailSent, setPasswordResetEmailSent] = useState(false);
  const { promiseInProgress } = usePromiseTracker({ delay: 500 });

  const handleEmailChange = (event: ChangeEvent<HTMLInputElement>) => {
    setEmail(event.target.value);
  };

  const submitHandler = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    // The server responds the same way whether or not an account exists (no
    // email enumeration), so we always advance to the confirmation screen on
    // success and only surface an error for genuine server/network failures.
    trackPromise(
      axiosClient
        .post("/api/users/send-password-reset", { email })
        .then(() => {
          setErrorMessage(null);
          setPasswordResetEmailSent(true);
        })
        .catch(() => {
          setErrorMessage("Something went wrong, please try again.");
        })
    );
  };

  const handleTryAgain = () => {
    setPasswordResetEmailSent(false);
  };

  return passwordResetEmailSent ? (
    <>
      <div className="flex flex-col py-12 px-4">
        <div className="w-full">
          <CheckCircleIcon
            className="h-auto w-12 mx-auto text-indigo-600"
            aria-hidden="true"
          />
          <h2 className="text-center mt-6 text-3xl font-bold tracking-tight text-gray-900">
            Check your email
          </h2>
          <p className="text-center text-sm text-gray-900 mt-2">
            If an account exists for that email, we've sent a link to reset your
            password.
          </p>
        </div>
      </div>
      <div className="p-4 w-full flex justify-between bg-gray-50 fixed bottom-0">
        <button
          onClick={handleTryAgain}
          className="text-sm text-indigo-600 font-medium flex"
        >
          <span className="flex items-center pr-1">
            <ArrowLongLeftIcon
              className="h-5 w-auto text-indigo-600"
              aria-hidden="true"
            />
          </span>
          Try Again
        </button>
        <Link to="/login" className="text-sm text-indigo-600 font-medium flex">
          Login
          <span className="flex items-center pl-1">
            <ArrowLongRightIcon
              className="h-5 w-auto text-indigo-600"
              aria-hidden="true"
            />
          </span>
        </Link>
      </div>
    </>
  ) : (
    <>
      <div className="flex py-12 px-4">
        <div className="w-full">
          <div>
            <div className="flex flex-col items-center mb-8">
              <img
                className="mx-auto w-16 h-auto"
                src="/superset-icon.png"
                alt="Superset"
              />
              <h1 className="mb-1 mt-1 font-bold leading-7 text-indigo-600 text-4xl tracking-tight">
                Superset
              </h1>
              <span className="font-normal text-med tracking-[1.4px] text-indigo-600">
                Workout Tracker
              </span>
            </div>
            <h2 className=" text-center text-2xl font-bold tracking-tight text-gray-900">
              Forgot Your Password?
            </h2>
            <p className="text-center text-sm text-gray-900 mt-2">
              Enter your email and we'll send you a link to reset your password.
            </p>
          </div>
          <form className="mt-8" onSubmit={submitHandler}>
            <div>
              <label htmlFor="emailAddress" className="sr-only">
                Email address
              </label>
              <input
                id="emailAddress"
                name="email"
                type="email"
                className="relative block w-full appearance-none rounded-md shadow-sm border border-gray-300 px-3 py-2 text-gray-900 placeholder-gray-500 focus:z-10 focus:border-indigo-500 focus:outline-none focus:ring-indigo-500 text-sm"
                placeholder="Email address"
                value={email}
                onChange={handleEmailChange}
              />
            </div>
            <div className="mt-1 min-h-[1.25rem]">
              {errorMessage && (
                <div className="text-sm text-red-600">{errorMessage}</div>
              )}
            </div>

            <div>
              <button
                type="submit"
                className="mt-4 group relative flex w-full justify-center rounded-md border border-transparent bg-indigo-600 py-2 px-4 text-sm font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:pointer-events-none"
                disabled={promiseInProgress ? true : false}
              >
                {!promiseInProgress && `Reset your password`}
                {promiseInProgress && (
                  <ClipLoader
                    color="rgb(white)"
                    speedMultiplier={0.6}
                    size={"1.25rem"}
                  />
                )}
              </button>
            </div>
          </form>
        </div>
        <div className=" px-4 py-4 w-full flex justify-center fixed bottom-0 bg-gray-50">
          <Link
            to="/login"
            className="text-sm text-indigo-600 font-medium flex"
          >
            Back to login
          </Link>
        </div>
      </div>
    </>
  );
};

export default ForgotPassword;
